import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../layouts/DashboardLayout";
import api from "../../api";
import { Card } from "../../components/ui/CCard";
import { usePeriodFilter, PeriodPicker, formatPeriodLabel } from "../../components/dashboard/financePeriod";
import { ArrowLeft, Loader2, Wallet, Coins, AlertTriangle, PiggyBank, Clock } from "lucide-react";

// Tailwind's JIT scanner needs full static class strings in source, so the
// icon-chip colors are spelled out per type rather than built from a
// template literal (a dynamic `bg-${color}-100` would silently not exist in
// the compiled CSS unless that exact string happened to appear elsewhere).
const TYPE_CONFIG = {
  net_payout: {
    title: "Net Payout",
    description: "Consultations whose money has actually been settled to you — at-clinic fees (received immediately) plus online payments ops has completed a payout for.",
    icon: Wallet,
    iconClass: "bg-blue-100 text-blue-600",
  },
  pending_payout: {
    title: "Pending Payout",
    description: "Collected online from patients, consultation completed, but not yet paid out to you by our ops team. Always shows everything currently owed across all time, regardless of the period filter.",
    icon: Coins,
    iconClass: "bg-amber-100 text-amber-600",
  },
  missed_revenue: {
    title: "Missed Revenue",
    description: "Money that will never reach you: appointments whose date has passed with no payment ever made, or paid online with no consultation ever recorded (ops will never pay those out without one).",
    icon: AlertTriangle,
    iconClass: "bg-red-100 text-red-600",
  },
  advance_collected: {
    title: "Advance Collected",
    description: "Paid online for an upcoming appointment that hasn't happened yet — already collected by the gateway, but too early to count as earned or missed. Moves to Net/Pending Payout once the consultation happens, or Missed Revenue if the date passes with no consultation.",
    icon: PiggyBank,
    iconClass: "bg-purple-100 text-purple-600",
  },
  pending_collections: {
    title: "Pending Collections",
    description: "Appointments in this period dated today or later that are still Unpaid — still realistically collectible. Doesn't include appointments whose date has already passed (see Missed Revenue).",
    icon: Clock,
    iconClass: "bg-amber-100 text-amber-600",
  },
};

// Online vs At Clinic, with the actual collection/settlement reference shown
// underneath when available - an at-clinic row shows what was captured by
// the "collect payment" flow (see Appointment.js); an online row shows the
// bank/RazorpayX reference ops recorded when settling its payout batch (see
// PayoutsAdmin.js's BatchRow), so an org can cross-check either kind of
// payment against their own records. A single detail list (e.g. Net Payout)
// can mix both kinds of rows together, hence the distinction here.
const PaymentModeBadge = ({ row }) => {
  const isOnline = row.mode === "Online";
  // Settled rows show the payout's own settlement reference; an online row
  // that hasn't been settled yet (e.g. Pending Payout) has none of those,
  // so it falls back to the order id - still enough to look the payment up.
  const reference = isOnline ? (row.payout_reference || row.order_id) : row.collected_payment_reference;
  return (
    <div className="leading-tight whitespace-nowrap">
      <span className={`text-xs font-semibold ${isOnline ? "text-blue-700" : "text-emerald-700"}`}>
        {isOnline ? "Online" : `At Clinic${row.collected_payment_method ? ` · ${row.collected_payment_method}` : ""}`}
      </span>
      {reference && (
        <span className="block text-[10px] font-mono text-secondary-400 truncate max-w-[160px]" title={reference}>
          Ref: {reference}
        </span>
      )}
    </div>
  );
};

// Jumps straight to the clinical record behind this row - absent for a row
// with no consultation yet (e.g. advance_collected, or a legacy at-clinic
// appointment created before consultations were linked).
const ConsultationLink = ({ consultationId, onClick }) => {
  if (!consultationId) {
    return <span className="text-xs text-secondary-300">—</span>;
  }
  return (
    <button
      type="button"
      onClick={() => onClick(consultationId)}
      className="text-xs font-semibold text-primary-600 hover:text-primary-800 hover:underline"
    >
      #{consultationId}
    </button>
  );
};

// A missed_revenue row may be a never-paid appointment (only `fee` set) or
// an online-paid one with no consultation ever recorded (`net_amount` set,
// matching the aggregate on the Finance tab) - prefer net_amount when present.
const rowAmount = (r) => (r.net_amount != null ? r.net_amount : r.fee);

const BATCH_STATUS_BADGE = {
  Initiated: { label: "Payout Initiated", bg: "bg-blue-100", text: "text-blue-700" },
  Processing: { label: "Processing", bg: "bg-blue-100", text: "text-blue-700" },
  Completed: { label: "Paid Out", bg: "bg-green-100", text: "text-green-700" },
};

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function PayoutDetails() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const type = searchParams.get("type");
  const doctorId = searchParams.get("doctorId") || null;
  const branchId = searchParams.get("branchId") || null;

  const { period, setPeriod, customStart, setCustomStart, customEnd, setCustomEnd, range } = usePeriodFilter({
    period: searchParams.get("period") || "this_month",
    customStart: searchParams.get("start") || "",
    customEnd: searchParams.get("end") || "",
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const config = TYPE_CONFIG[type];

  useEffect(() => {
    if (!config || !range || range.end < range.start) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getPayoutDetails({
        type,
        doctor_id: doctorId || undefined,
        branch_id: branchId || undefined,
        start_date: range.start,
        end_date: range.end,
      })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        console.error("PayoutDetails: failed to load", err);
        if (!cancelled) setError("Unable to load this data right now.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [type, doctorId, branchId, period, customStart, customEnd]);

  if (!config) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center text-secondary-400">Unknown detail type.</div>
      </DashboardLayout>
    );
  }

  const Icon = config.icon;

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in-up">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-secondary-500 hover:text-primary-600 transition-colors"
        >
          <ArrowLeft size={16} /> Back to Finance
        </button>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`p-3 rounded-2xl ${config.iconClass}`}>
              <Icon size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-secondary-900">{config.title}</h1>
              <p className="text-secondary-500 text-sm mt-1 max-w-xl">{config.description}</p>
            </div>
          </div>
        </div>

        {type === "pending_payout" ? (
          <p className="text-sm text-secondary-400 italic">Showing all-time data — Pending Payout isn't limited to a period, since it's everything currently owed to you.</p>
        ) : (
          <PeriodPicker
            period={period}
            onPeriodChange={setPeriod}
            customStart={customStart}
            onCustomStartChange={setCustomStart}
            customEnd={customEnd}
            onCustomEndChange={setCustomEnd}
            resolvedLabel={data?.period ? `${formatPeriodLabel(data.period.start_date)} – ${formatPeriodLabel(data.period.end_date)}` : null}
          />
        )}

        {loading ? (
          <Card className="p-12 flex flex-col items-center justify-center text-secondary-400">
            <Loader2 className="w-6 h-6 animate-spin mb-2" />
            Loading...
          </Card>
        ) : error || !data ? (
          <Card className="p-12 text-center text-secondary-400">{error || "No data available."}</Card>
        ) : (
          <>
            <Card className="p-5 flex items-center justify-between">
              <span className="text-secondary-500 text-sm">
                {data.count} consultation{data.count === 1 ? "" : "s"}
              </span>
              <span className="text-2xl font-heading font-bold text-secondary-900">{money(data.total_amount)}</span>
            </Card>

            <Card className="overflow-hidden">
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-secondary-50/50 border-b border-secondary-100 text-xs uppercase tracking-wider text-secondary-500 font-semibold">
                      <th className="px-6 py-2.5">Date</th>
                      <th className="px-6 py-2.5">Patient</th>
                      <th className="px-6 py-2.5">Doctor</th>
                      <th className="px-6 py-2.5">Consultation</th>
                      <th className="px-6 py-2.5">Payment Mode</th>
                      <th className="px-6 py-2.5 text-right">Amount</th>
                      {type !== "missed_revenue" && type !== "advance_collected" && type !== "pending_collections" && <th className="px-6 py-2.5 text-right">Status</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100">
                    {data.rows.length > 0 ? (
                      data.rows.map((r) => (
                        <tr key={r.appointment_id} className="hover:bg-secondary-50/30 transition-colors">
                          <td className="px-6 py-2 text-sm text-secondary-500">{r.date}</td>
                          <td className="px-6 py-2 font-medium text-secondary-900">{r.patient_name || "Unknown"}</td>
                          <td className="px-6 py-2 text-secondary-600">{r.doctor_name || "Unknown"}</td>
                          <td className="px-6 py-2">
                            <ConsultationLink consultationId={r.consultation_id} onClick={(id) => navigate(`/consultation/${id}`)} />
                          </td>
                          <td className="px-6 py-2"><PaymentModeBadge row={r} /></td>
                          <td className="px-6 py-2 text-right font-semibold text-secondary-900">
                            {money(rowAmount(r))}
                          </td>
                          {type !== "missed_revenue" && type !== "advance_collected" && type !== "pending_collections" && (
                            <td className="px-6 py-2 text-right">
                              {r.batch_status && BATCH_STATUS_BADGE[r.batch_status] ? (
                                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${BATCH_STATUS_BADGE[r.batch_status].bg} ${BATCH_STATUS_BADGE[r.batch_status].text}`}>
                                  {BATCH_STATUS_BADGE[r.batch_status].label}
                                </span>
                              ) : type === "pending_payout" ? (
                                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-500">
                                  Not Yet Initiated
                                </span>
                              ) : (
                                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">
                                  Received
                                </span>
                              )}
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={type === "missed_revenue" || type === "advance_collected" || type === "pending_collections" ? 6 : 7} className="px-6 py-12 text-center text-secondary-400 italic">
                          No consultations found for this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden divide-y divide-secondary-100">
                {data.rows.length > 0 ? (
                  data.rows.map((r) => (
                    <div key={r.appointment_id} className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-bold text-secondary-900 text-base">{r.patient_name || "Unknown"}</div>
                          <div className="text-xs text-secondary-500 mt-1">{r.doctor_name} · {r.date}</div>
                        </div>
                        <span className="font-semibold text-secondary-900">{money(rowAmount(r))}</span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <PaymentModeBadge row={r} />
                        <ConsultationLink consultationId={r.consultation_id} onClick={(id) => navigate(`/consultation/${id}`)} />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-secondary-400 italic">No consultations found for this period.</div>
                )}
              </div>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
